import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Skeleton,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import FeedLayout from '../../components/feed/FeedLayout';
import { getFeedSummary, getRegions } from '../../services/feedApi';

const StatCard = ({ label, value, subtitle, loading }) => (
  <Card
    sx={{
      p: 3,
      border: '1px solid',
      borderColor: 'divider',
      height: '100%',
    }}
  >
    {loading ? (
      <>
        <Skeleton variant="text" width="60%" height={20} />
        <Skeleton variant="text" width="40%" height={48} />
        <Skeleton variant="text" width="80%" height={16} />
      </>
    ) : (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {label}
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1a2b4a', mb: 0.5 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </>
    )}
  </Card>
);

const RegionRow = ({ region, totalLocations, onClick }) => {
  const percentage = totalLocations > 0 && region.locationCount != null
    ? Math.round((region.locationCount / totalLocations) * 100)
    : null;

  return (
    <ListItem
      button
      onClick={onClick}
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'rgba(59, 89, 152, 0.04)' },
      }}
    >
      <ListItemText
        primary={
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1a2b4a' }}>
            {region.name}
          </Typography>
        }
        secondary={`${region.locationCount ?? 0} locations`}
      />
      <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {percentage != null && (
          <Chip
            label={`${percentage}%`}
            size="small"
            sx={{ bgcolor: 'rgba(59, 89, 152, 0.1)', color: '#3b5998' }}
          />
        )}
        <IconButton edge="end" onClick={onClick}>
          <ChevronRightIcon />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

const FeedOverview = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [summary, setSummary] = useState(null);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [summaryData, regionsData] = await Promise.all([
          getFeedSummary(),
          getRegions(),
        ]);
        setSummary(summaryData);
        setRegions(regionsData.regions || []);
      } catch (err) {
        console.error('Error fetching overview data:', err);
        const status = err.response?.status;
        
        if (status === 401 || status === 403) {
          return;
        }
        
        setError(
          status === 404
            ? 'Feed API not available. Connect the server to see portfolio data.'
            : `Failed to load overview data: ${err.message}`
        );
        setSummary(null);
        setRegions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleRegionClick = (regionId) => {
    navigate(`/feed/regions/${regionId}`);
  };

  return (
    <FeedLayout
      title="Overview"
      subtitle="Location information and portfolio metrics"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
            },
            '& .Mui-selected': {
              color: '#3b5998',
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#3b5998',
            },
          }}
        >
          <Tab label="Locations" />
          <Tab label="Reviews" />
        </Tabs>
      </Box>

      {activeTab === 0 && (() => {
        const regionCount = summary?.regionCount ?? Object.keys(summary?.regions || {}).length;
        const totalLocations = summary?.totalLocations ?? 0;
        const avgLocationsPerRegion = regionCount > 0 ? Math.round(totalLocations / regionCount) : 0;

        return (
        <>
          {/* Location Stats */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <StatCard
                label="Total Locations"
                value={totalLocations}
                subtitle={`${regionCount} regions`}
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                label="Active Regions"
                value={regionCount}
                subtitle="Geographic coverage"
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                label="Avg Locations Per Region"
                value={avgLocationsPerRegion}
                subtitle="Distribution"
                loading={loading}
              />
            </Grid>
          </Grid>

          {/* Regions List */}
          <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: 0 }}>
              {loading ? (
                <Box sx={{ p: 3 }}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
                  ))}
                </Box>
              ) : regions.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No regions available. Connect the Feed API to view portfolio data.
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {regions.map((region) => (
                    <RegionRow
                      key={region.name}
                      region={region}
                      totalLocations={totalLocations}
                      onClick={() => handleRegionClick(region.id)}
                    />
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </>
        );
      })()}

      {activeTab === 1 && (() => {
        const averageRating = summary?.reviewTotals?.averageRating;
        const hasAverageRating = Number.isFinite(averageRating);

        return (
        <>
          {/* Review Stats */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <StatCard
                label="Total Reviews"
                value={(summary?.reviewTotals?.totalReviews ?? 0).toLocaleString()}
                subtitle="All time"
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card
                sx={{
                  p: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                }}
              >
                {loading ? (
                  <Skeleton variant="rectangular" height={80} />
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Average Rating
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1a2b4a' }}>
                        {hasAverageRating ? averageRating : 'N/A'}
                      </Typography>
                      {hasAverageRating && <StarIcon sx={{ color: '#f59e0b', fontSize: 32 }} />}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Portfolio average
                    </Typography>
                  </>
                )}
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card
                sx={{
                  p: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                }}
              >
                {loading ? (
                  <Skeleton variant="rectangular" height={80} />
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Data Freshness
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1a2b4a' }}>
                        {summary?.snapshotFreshness ?? 'N/A'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Snapshot timestamp
                    </Typography>
                  </>
                )}
              </Card>
            </Grid>
          </Grid>

          {/* Rating Distribution */}
          <Card sx={{ border: '1px solid', borderColor: 'divider', p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a2b4a', mb: 3 }}>
              Rating Distribution
            </Typography>
            {loading ? (
              <Box>
                {[5, 4, 3, 2, 1].map((star) => (
                  <Skeleton key={star} variant="rectangular" height={32} sx={{ mb: 1, borderRadius: 1 }} />
                ))}
              </Box>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No rating breakdown available
                </Typography>
              </Box>
            )}
          </Card>
        </>
        );
      })()}
    </FeedLayout>
  );
};

export default FeedOverview;
